import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle2, AlertCircle, Lightbulb, Sprout, Flame, Scale, Crown, Skull, MousePointerClick } from 'lucide-react';

interface BaseMechanicShellProps {
  lessonSlug: string;
  mechanicId: string;
  prompt: string;
  icon: React.ReactNode;
  payload?: any;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: {
    kind?: string;
    correctAnswer?: string;
    correctSequence?: string[];
    matches?: Record<string, string>;
    hints?: any[];
  };
  iconLabel?: string;
  actionsLeft?: number;
  children: (
    submissionState: 'idle' | 'submitting' | 'error' | 'success',
    setAnswer: (ans: any) => void,
    hintRevealed: boolean
  ) => React.ReactNode;
}

const TIER_ICONS: Record<string, React.ReactNode> = {
  Easy: <Sprout className="w-3.5 h-3.5" />,
  Medium: <Scale className="w-3.5 h-3.5" />,
  Hard: <Flame className="w-3.5 h-3.5" />,
  Epic: <Crown className="w-3.5 h-3.5" />,
  Impossible: <Skull className="w-3.5 h-3.5" />,
};

const TIER_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  Easy: { bg: '#22c55e15', text: '#16a34a', border: '#22c55e30' },
  Medium: { bg: '#f59e0b15', text: '#d97706', border: '#f59e0b30' },
  Hard: { bg: '#ef444415', text: '#dc2626', border: '#ef444430' },
  Epic: { bg: '#a855f715', text: '#9333ea', border: '#a855f730' },
  Impossible: { bg: '#ec489915', text: '#db2777', border: '#ec489930' },
};

async function postGamification<T>(url: string, payload: unknown): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.message || 'Gamification request failed.');
  }

  return data as T;
}

export default function BaseMechanicShell({ lessonSlug, mechanicId, prompt, icon, payload, tier, localHints, localValidator, iconLabel, actionsLeft, children }: BaseMechanicShellProps) {
  const [userAnswer, setUserAnswer] = useState<any>(null);
  const [submissionState, setSubmissionState] = useState<'idle' | 'submitting' | 'error' | 'success'>('idle');
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [currentHint, setCurrentHint] = useState<string | null>(null);
  const [hintLoading, setHintLoading] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [tierHovered, setTierHovered] = useState(false);
  const variantLessonSlug = payload?._variantLessonSlug;
  const shouldPersistLocalSolve = payload?._persistLocalSolve === true;
  const tierMechanicIds = Array.isArray(payload?._tierMechanicIds) ? payload._tierMechanicIds : [];
  const submitLockRef = useRef(false);
  const timeoutIdsRef = useRef<number[]>([]);

  const scheduleTimeout = (callback: () => void | Promise<void>, delayMs: number) => {
    const timeoutId = window.setTimeout(() => {
      timeoutIdsRef.current = timeoutIdsRef.current.filter((id) => id !== timeoutId);
      void callback();
    }, delayMs);
    timeoutIdsRef.current.push(timeoutId);
  };

  const clearPendingTimeouts = () => {
    for (const timeoutId of timeoutIdsRef.current) {
      window.clearTimeout(timeoutId);
    }
    timeoutIdsRef.current = [];
  };

  const releaseSubmitLock = () => {
    submitLockRef.current = false;
  };

  const finishErrorState = (message: string) => {
    setSubmissionState('error');
    setFeedbackMessage(message);
    setShakeKey((previous) => previous + 1);
    scheduleTimeout(() => {
      setSubmissionState('idle');
      setFeedbackMessage(null);
      releaseSubmitLock();
    }, 1800);
  };

  useEffect(() => {
    return () => {
      clearPendingTimeouts();
      releaseSubmitLock();
    };
  }, []);

  const notifySolved = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('classes:lesson-mechanic-solved'));
    }
  };

  const handleSubmit = async () => {
    if (!userAnswer || submissionState !== 'idle' || submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmissionState('submitting');
    setFeedbackMessage(null);
    setCurrentHint(null);

    // Lab mode: check answer locally against the private validator
    if (localValidator) {
      let isCorrect = false;

      if (localValidator.kind === 'exact' && localValidator.correctAnswer) {
        isCorrect = String(userAnswer).trim() === String(localValidator.correctAnswer).trim();
      } else if (localValidator.kind === 'sequence' && localValidator.correctSequence) {
        const submitted = typeof userAnswer === 'string'
          ? userAnswer.split(',').map((s: string) => s.trim())
          : userAnswer;
        const expected = localValidator.correctSequence;
        isCorrect = submitted.length === expected.length
          && submitted.every((id: string, i: number) => id === expected[i]);
      } else if (localValidator.kind === 'mapping' && localValidator.matches) {
        const submitted = typeof userAnswer === 'string' ? JSON.parse(userAnswer) : userAnswer;
        const expected = localValidator.matches;
        const keys = Object.keys(expected);
        isCorrect = keys.length > 0 && keys.every((k: string) => submitted?.[k] === expected[k]);
      } else {
        // invariants, rubric — frontend inner component already validated
        isCorrect = true;
      }

      scheduleTimeout(async () => {
        if (isCorrect) {
          setSubmissionState('success');
          setFeedbackMessage('Correct! (Local check — no MP awarded)');

          try {
            await Promise.all([
              new Promise((resolve) => scheduleTimeout(resolve as () => void, 1200)),
              shouldPersistLocalSolve && variantLessonSlug
                ? postGamification('/api/classes/gamification/record-local-solve', {
                    lessonSlug,
                    variantLessonSlug,
                    mechanicId,
                    tierMechanicIds,
                  })
                : Promise.resolve(),
            ]);
          } catch {
            setFeedbackMessage('Correct locally, but progress sync failed.');
            releaseSubmitLock();
            return;
          }

          notifySolved();
          releaseSubmitLock();
          return;
        }

        finishErrorState('Incorrect. Try again.');
      }, 400);
      return;
    }

    // Production mode: server action
    try {
      const response = await postGamification<any>('/api/classes/gamification/submit', {
        lessonSlug,
        variantLessonSlug,
        mechanicId,
        userAnswer: typeof userAnswer === 'object' ? JSON.stringify(userAnswer) : String(userAnswer),
      });

      if (response.success && response.isCorrect) {
        setSubmissionState('success');
        setFeedbackMessage(response.completedTier
          ? `${response.completedTier} completed! ${response.alreadySolved ? 'Already mastered!' : `+${response.mpAwarded || 0} MP`}`
          : (response.alreadySolved ? 'Already mastered!' : `Mastered! +${response.mpAwarded || 0} MP`));
        notifySolved();
        releaseSubmitLock();
      } else {
        finishErrorState(response.message || 'Incorrect. Try again.');
      }
    } catch (error) {
      finishErrorState('Network error. Try again.');
    }
  };

  const handleRevealHint = async () => {
    if (submissionState !== 'idle') {
      return;
    }

    setHintLoading(true);
    // Use local hints when available (Lab mode), otherwise call server action
    if (localHints && localHints.length > 0) {
      const hint = localHints[0].hint || 'No hint available.';
      scheduleTimeout(() => {
        setCurrentHint(hint);
        setHintLoading(false);
      }, 300);
      return;
    }
    try {
      const response = await postGamification<any>('/api/classes/gamification/hint', { lessonSlug, variantLessonSlug, mechanicId });
      if (response.success && response.hint) {
        setCurrentHint(response.hint);
      } else {
        setCurrentHint('No hints available.');
      }
    } catch {
      setCurrentHint('Could not load hint.');
    }
    setHintLoading(false);
  };

  const showHintButton = !!(payload?._hasHints) || !!(localHints?.length);
  const isSuccess = submissionState === 'success';
  const isError = submissionState === 'error';
  const isInteractionLocked = submissionState !== 'idle';

  return (
    <motion.div 
      key={shakeKey}
      initial={false}
      animate={isError ? { x: [-10, 10, -10, 10, 0] } : isSuccess ? { scale: [1, 1.02, 1] } : {}}
      transition={isError ? { duration: 0.4 } : isSuccess ? { duration: 0.5 } : {}}
      className={`my-8 p-6 rounded-2xl border-2 transition-colors duration-500 shadow-sm relative ${
        isSuccess 
          ? 'bg-primary/10 border-primary/50 shadow-[0_0_30px_rgba(var(--primary),0.2)]' 
          : isError
          ? 'bg-destructive/5 border-destructive/30'
          : 'bg-muted/30 border-border/50 hover:border-border'
      }`}
    >
      {tier && (
        <div
          className="absolute top-3 right-3"
          title={tier}
          onMouseEnter={() => setTierHovered(true)}
          onMouseLeave={() => setTierHovered(false)}
        >
          <div
            className="flex items-center justify-center w-6 h-6 rounded-full border transition-all"
            style={{
              opacity: tierHovered ? 1 : 0.3,
              color: tierHovered ? TIER_COLORS[tier]?.text : undefined,
              background: tierHovered ? TIER_COLORS[tier]?.bg : undefined,
              borderColor: tierHovered ? TIER_COLORS[tier]?.border : undefined,
            }}
          >
            {TIER_ICONS[tier] || <Scale className="w-3.5 h-3.5" />}
          </div>
        </div>
      )}
      <div className="flex items-start gap-4 mb-6">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 cursor-help ${
            isSuccess ? 'bg-primary text-primary-foreground' : isError ? 'bg-destructive text-destructive-foreground' : 'bg-primary/10 text-primary'
          }`}
          title={iconLabel || prompt}
        >
          {isSuccess ? <CheckCircle2 className="w-5 h-5" /> : isError ? <AlertCircle className="w-5 h-5" /> : icon}
        </div>
        <h3 className="text-base font-bold text-foreground leading-snug pt-1 pr-8">{prompt}</h3>
      </div>

      <div className="mb-6">
        {children(submissionState, setUserAnswer, currentHint !== null)}
      </div>

      <div className="mt-6 flex items-center justify-between min-h-[44px] gap-3">
        <div className="flex-1">
          <AnimatePresence mode="wait">
            {feedbackMessage && (
              <motion.p
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`text-sm font-medium ${isSuccess ? 'text-primary' : isError ? 'text-destructive' : 'text-muted-foreground'}`}
              >
                {feedbackMessage}
              </motion.p>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {currentHint && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-2 flex items-start gap-2 text-base text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 border border-amber-200 dark:border-amber-800"
              >
                <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{currentHint}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {actionsLeft !== undefined && !isSuccess && (
            <div
              className="flex items-center gap-1.5 px-2 py-1 rounded-full"
              title={`${actionsLeft} action${actionsLeft !== 1 ? 's' : ''} left`}
            >
              <MousePointerClick className="w-3 h-3 text-foreground/50" />
              <span className="text-xs font-mono font-bold text-foreground/80">{actionsLeft}</span>
            </div>
          )}
          {showHintButton && !isSuccess && (
            <button
              type="button"
              onClick={handleRevealHint}
              disabled={hintLoading || !!currentHint || isInteractionLocked}
              className={`p-2.5 rounded-xl transition-all ${
                currentHint || isInteractionLocked
                  ? 'text-muted-foreground/20 cursor-not-allowed'
                  : 'text-foreground/40 hover:text-amber-500 hover:shadow-[0_0_10px_rgba(245,158,11,0.3)] hover:bg-amber-50/50 dark:hover:bg-amber-950/20'
              }`}
              title="Reveal hint"
            >
              <Lightbulb className={`w-4 h-4 ${hintLoading ? 'animate-pulse' : ''}`} />
            </button>
          )}
          <button
            type="button"
            disabled={!userAnswer || isInteractionLocked}
            onClick={handleSubmit}
            className={`
              px-6 py-2.5 rounded-xl font-bold text-sm tracking-wide transition-all min-w-[120px] flex items-center justify-center
              focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background
              ${
                isSuccess
                  ? 'bg-primary text-primary-foreground opacity-50 cursor-not-allowed'
                  : !userAnswer || isInteractionLocked
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-primary text-primary-foreground hover:brightness-110 shadow-md hover:shadow-lg active:scale-95'
              }
            `}
          >
            {submissionState === 'submitting' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isSuccess ? (
              'Done'
            ) : isError ? (
              'Wait'
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
